/**
 * Adaptive price formatting.
 *
 * Cases:
 *  - per_person: `€75/pax` (or "€75 por persona" in es-long)
 *  - per_group : `desde €140/pax` (derives min €/pax from tiers[0])
 *  - on_request: "On request" / "Bajo petición"
 *  - No pricing fields at all (user's show_prices=false or missing data): null
 */

const CURRENCY_SIGN = { EUR: "€", USD: "$", GBP: "£" };

const T = {
  from: { en: "from", es: "desde" },
  pax: { en: "/pax", es: "/pax" },
  onRequest: { en: "On request", es: "Bajo petición" },
  perGroupSuffix: {
    en: (n) => ` group of ${n}`,
    es: (n) => ` grupo de ${n}`,
  },
};

function fmtNumber(n, currency = "EUR") {
  const sign = CURRENCY_SIGN[currency] || "";
  const rounded = Number.isInteger(n) ? n : Math.round(n);
  return `${sign}${rounded}`;
}

/** Compact string for cards (short line). Returns null if no price info. */
export function formatPriceShort(exp, lang = "en") {
  if (!exp) return null;
  const unit = exp.price_unit;
  const cur = exp.currency || "EUR";

  if (unit === "on_request") return T.onRequest[lang];
  if (unit === "per_person" && exp.price != null) {
    return `${fmtNumber(exp.price, cur)}${T.pax[lang]}`;
  }
  if (unit === "per_group" && Array.isArray(exp.price_tiers) && exp.price_tiers.length > 0) {
    const t = exp.price_tiers[0];
    const perPax = t.price / t.max_pax;
    return `${T.from[lang]} ${fmtNumber(perPax, cur)}${T.pax[lang]}`;
  }
  // Legacy fallback: bare `price` field, treat as per_person
  if (exp.price != null) return `${fmtNumber(exp.price, cur)}${T.pax[lang]}`;
  return null;
}

/** Rich payload for detail page: label + rows (for tiers) + notes. */
export function priceDetail(exp, lang = "en") {
  if (!exp) return null;
  const unit = exp.price_unit;
  const cur = exp.currency || "EUR";

  const notesLocalized = (() => {
    const n = exp.price_notes;
    if (!n) return null;
    if (typeof n === "string") return n;
    if (typeof n === "object") return n[lang] || n.en || null;
    return null;
  })();

  if (unit === "on_request") {
    return { kind: "on_request", label: T.onRequest[lang], notes: notesLocalized };
  }
  if (unit === "per_person" && exp.price != null) {
    return {
      kind: "per_person",
      label: `${fmtNumber(exp.price, cur)}${T.pax[lang]}`,
      notes: notesLocalized,
    };
  }
  if (unit === "per_group" && Array.isArray(exp.price_tiers) && exp.price_tiers.length > 0) {
    return {
      kind: "per_group",
      label: null, // caller renders table
      tiers: exp.price_tiers.map((t) => ({
        max_pax: t.max_pax,
        price: t.price,
        label: `${fmtNumber(t.price, cur)}${T.perGroupSuffix[lang](t.max_pax)}`,
        rawSign: CURRENCY_SIGN[cur] || "",
      })),
      notes: notesLocalized,
    };
  }
  return null;
}
